import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid3x3, KeyRound, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { AppModal } from '@/components/common/AppModal'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth.store'
import { PasswordInput } from '@/modules/auth/components/PasswordFields'
import { PinInput } from '@/modules/auth/components/PinInput'
import { authService } from '@/modules/auth/services/auth.service'
import { useStepUpStore } from '@/modules/auth/lock/step-up'
import { apiErrorCode } from '@/services/api/errors'
import { ROUTES } from '@/constants/routes'

/** "Confirm it's you" before sensitive actions (see step-up.ts) — PIN first, password as fallback. */
export function StepUpDialog() {
  const { open, resolve } = useStepUpStore()
  const { user } = useAuth()
  const clearSession = useAuthStore((s) => s.clearSession)
  const navigate = useNavigate()
  const pinSet = Boolean(user?.security?.pinSet)
  const [usePassword, setUsePassword] = useState(false)
  const mode = pinSet && !usePassword ? 'pin' : 'password'
  const [pin, setPin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setPin('')
    setPassword('')
    setError(null)
  }

  const finish = (ok: boolean) => {
    reset()
    setUsePassword(false)
    resolve?.(ok)
  }

  const submit = async (input: { pin: string } | { password: string }) => {
    setBusy(true)
    setError(null)
    try {
      await authService.stepUp(input)
      finish(true)
    } catch (err) {
      if (apiErrorCode(err) === 'SESSION_ENDED') {
        finish(false)
        clearSession()
        toast.error('Too many wrong tries. Sign in again.')
        navigate(ROUTES.LOGIN, { replace: true })
        return
      }
      setError(err instanceof Error ? err.message : 'Could not confirm')
      setPin('')
      setShakeKey((k) => k + 1)
    } finally {
      setBusy(false)
    }
  }

  const switchMode = () => {
    setUsePassword(mode === 'pin')
    reset()
  }

  return (
    <AppModal
      open={open}
      onOpenChange={(next) => {
        if (!next) finish(false)
      }}
      title="Confirm it's you"
      icon={ShieldCheck}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => finish(false)} disabled={busy}>
            Cancel
          </Button>
          {mode === 'password' ? (
            <Button onClick={() => void submit({ password })} loading={busy} disabled={!password}>
              Confirm
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        {mode === 'pin' ? (
          <PinInput
            value={pin}
            onChange={(v) => {
              setPin(v)
              setError(null)
            }}
            onComplete={(v) => void submit({ pin: v })}
            disabled={busy}
            error={Boolean(error)}
            shakeKey={shakeKey}
            label="PIN"
          />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (password) void submit({ password })
            }}
          >
            <PasswordInput
              autoFocus
              withIcon
              autoComplete="current-password"
              placeholder="Password"
              className="h-10"
              aria-invalid={Boolean(error)}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError(null)
              }}
            />
            <button type="submit" hidden aria-hidden tabIndex={-1} />
          </form>
        )}
        <p
          className={error ? 'text-center text-xs text-destructive' : 'text-center text-xs text-muted-foreground'}
          role={error ? 'alert' : undefined}
        >
          {error ?? 'Valid for 15 min'}
        </p>
        {pinSet ? (
          <div className="flex justify-center">
            <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={switchMode} disabled={busy}>
              {mode === 'pin' ? <KeyRound aria-hidden /> : <Grid3x3 aria-hidden />}
              {mode === 'pin' ? 'Use password' : 'Use PIN'}
            </Button>
          </div>
        ) : null}
      </div>
    </AppModal>
  )
}
